import Instense from "./Axios-config";

export interface SearchResult {
  id: number;
  type: 'customer' | 'vendor' | 'order' | 'invoice' | 'jobOrder' | 'quotation' |
        'bank' | 'workstation' | 'location' | 'process' | 'jobTemplate' | 'priceBreakdown' | 
        'creditCard' | 'chartOfAccount' | 'vendorOrder' | 'vendorInvoice' | 
        'vendorReceiving' | 'vendorQuotation' | 'shipment' | 'ncrReport' | 'user' | 'document';
  name?: string;
  code?: string;
  email?: string;
  phone?: string;
  orderNumber?: number;
  displayNumber?: string;
  customerName?: string;
  customerPoNumber?: string;
  invoiceNumber?: string;
  jobOrderNumber?: number;
  quotationNumber?: number;
  partNo?: string;
  partName?: string;
  jobNumber?: string;
  totalAmount?: number;
  orderDate?: string;
  invoiceDate?: string;
  dueDate?: string;
  status?: string;
  // Additional fields for new types
  accountNo?: string;
  displayName?: string;
  vendorName?: string;
  vendorPoNumber?: string;
  shipmentNo?: string;
  trackingNo?: string;
  shipVia?: string;
  ncrNumber?: string;
  title?: string;
  severity?: string;
  firstName?: string;
  lastName?: string;
  userName?: string;
  empCode?: string;
  city?: string;
  state?: string;
  description?: string;
  ledgerCode?: string;
  nickName?: string;
  lastFourDigits?: string;
  cardType?: string;
  accountType?: string;
  isActive?: boolean;
  receivedDate?: string;
  receivedQty?: number;
  reportedDate?: string;
  // Document fields
  documentNumber?: string;
  categoryName?: string;
  requiresVersionControl?: boolean;
  currentVersionNumber?: number;
  isDocumentNumberAutoGenerated?: boolean;
}

export interface GlobalSearchResults {
  customers: SearchResult[];
  vendors: SearchResult[];
  orders: SearchResult[];
  invoices: SearchResult[];
  jobOrders: SearchResult[];
  quotations: SearchResult[];
  banks: SearchResult[];
  workstations: SearchResult[];
  locations: SearchResult[];
  processes: SearchResult[];
  jobTemplates: SearchResult[];
  priceBreakdowns: SearchResult[];
  creditCards: SearchResult[];
  chartOfAccounts: SearchResult[];
  vendorOrders: SearchResult[];
  vendorInvoices: SearchResult[];
  vendorReceiving: SearchResult[];
  vendorQuotations: SearchResult[];
  shipments: SearchResult[];
  ncrReports: SearchResult[];
  users: SearchResult[];
  documents: SearchResult[];
}

export class GlobalSearchService {
  public static async Search(query: string, tenantId: number, limit: number = 10): Promise<GlobalSearchResults> {
    if (!query || query.trim() === '') {
      return {
        customers: [],
        vendors: [],
        orders: [],
        invoices: [],
        jobOrders: [],
        quotations: [],
        banks: [],
        workstations: [],
        locations: [],
        processes: [],
        jobTemplates: [],
        priceBreakdowns: [],
        creditCards: [],
        chartOfAccounts: [],
        vendorOrders: [],
        vendorInvoices: [],
        vendorReceiving: [],
        vendorQuotations: [],
        shipments: [],
        ncrReports: [],
        users: [],
        documents: []
      };
    }

    const url = `/GlobalSearch/Search`;
    const response = await Instense.get(url, {
      params: { query: query.trim(), tenantId, limit }
    });
    return response.data;
  }

  public static getResultUrl(result: SearchResult): string {
    switch (result.type) {
      case 'customer':
        return `/masters/customer?open=${result.id}`;
      case 'vendor':
        return `/masters/vendor?open=${result.id}`;
      case 'order':
        return `/orders/customer?open=${result.id}`;
      case 'invoice':
        return `/orders/customer-invoices?open=${result.id}`;
      case 'jobOrder':
        return `/job-orders?open=${result.id}`;
      case 'quotation':
        return `/quotations/customer?open=${result.id}`;
      case 'bank':
        return `/masters/bank?open=${result.id}`;
      case 'workstation':
        return `/masters/workstation?open=${result.id}`;
      case 'location':
        return `/masters/location?open=${result.id}`;
      case 'process':
        return `/masters/process?open=${result.id}`;
      case 'jobTemplate':
        return `/masters/jobtemplate?open=${result.id}`;
      case 'priceBreakdown':
        return `/masters/pricebreakdown?open=${result.id}`;
      case 'creditCard':
        return `/masters/creditcard?open=${result.id}`;
      case 'chartOfAccount':
        return `/masters/chartofaccounts?open=${result.id}`;
      case 'vendorOrder':
        return `/purchasing/vendor-orders?open=${result.id}`;
      case 'vendorInvoice':
        return `/purchasing/vendor-invoices?open=${result.id}`;
      case 'vendorReceiving':
        return `/purchasing/vendor-receiving?open=${result.id}`;
      case 'vendorQuotation':
        return `/quotations/vendor?open=${result.id}`;
      case 'shipment':
        return `/orders/customer-shipments?open=${result.id}`;
      case 'ncrReport':
        return `/quality?open=${result.id}`;
      case 'user':
        return `/user-management?open=${result.id}`;
      case 'document':
        return `/documents?open=${result.id}`;
      default:
        return '/home';
    }
  }

  public static getResultDisplayName(result: SearchResult): string {
    switch (result.type) {
      case 'customer':
      case 'vendor':
        return result.name || result.code || '';
      case 'order':
        return result.displayNumber || `CO#${result.orderNumber}`;
      case 'invoice':
        return result.invoiceNumber || '';
      case 'jobOrder':
        return result.displayNumber || `JO#${result.jobOrderNumber}`;
      case 'quotation':
        return result.displayNumber || `CQ#${result.quotationNumber}`;
      case 'bank':
        return result.name || result.displayName || result.code || '';
      case 'workstation':
        return result.name || '';
      case 'location':
        return result.name || result.code || '';
      case 'process':
        return result.name || '';
      case 'jobTemplate':
        return result.code ? `${result.code} — ${result.name}` : result.name || '';
      case 'priceBreakdown':
        return result.name || '';
      case 'creditCard':
        return result.name || result.nickName || '';
      case 'chartOfAccount':
        return result.name || result.code || '';
      case 'vendorOrder':
        return result.displayNumber || `VO#${result.orderNumber}`;
      case 'vendorInvoice':
        return result.invoiceNumber || '';
      case 'vendorReceiving':
        return `Receiving #${result.id}`;
      case 'vendorQuotation':
        return result.displayNumber || `VQ#${result.quotationNumber}`;
      case 'shipment':
        return result.shipmentNo || result.trackingNo || '';
      case 'ncrReport':
        return result.ncrNumber || result.title || '';
      case 'user':
        return `${result.firstName || ''} ${result.lastName || ''}`.trim() || result.userName || '';
      case 'document':
        return result.documentNumber || result.name || '';
      default:
        return '';
    }
  }
}

